import React, { FunctionComponent } from "react";
import styled from "styled-components";
import {
  XSmall,
  CheckmarkCircle,
  ExclamationCircle,
} from "@vitalsource/vstui-icons";
import { red, green, cloud } from "@vitalsource/vstui/colors";
import { Button } from "@vitalsource/vstui";
import { useIntl } from "react-intl";
import { createThemedComponent } from "@vitalsource/vstui/themes";

const CLOSE_SIZE = 48;

export enum ToastType {
  SUCCESS,
  ERROR,
  NEUTRAL,
}

export interface ToastProps {
  type: ToastType;
  onClose: () => void;
}

const colorMap = {
  [ToastType.SUCCESS]: green.getShade(6),
  [ToastType.ERROR]: red.getShade(6),
  [ToastType.NEUTRAL]: cloud.getShade(6),
};

const iconMap = {
  [ToastType.SUCCESS]: <CheckmarkCircle />,
  [ToastType.ERROR]: <ExclamationCircle />,
  [ToastType.NEUTRAL]: null,
};

const Styled = {
  OuterContainer: styled.div<{ type: ToastType }>`
    border-radius: 4px;
    background: #fff;
    position: relative;
    display: inline-flex;
    font-size: 1.4rem;
    line-height: 1.5;
    font-weight: 500;
    box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.2);
    border-left: 4px solid ${props => colorMap[props.type]};
    width: 360px;
    color: ${props => colorMap[props.type]};
    padding-left: 16px;
  `,
  IconContainer: styled.div`
    flex: 0;
    &:not(:empty) {
      padding-right: 12px;
      padding-top: 16px;
    }
    svg {
      vertical-align: middle;
    }
  `,
  TextContainer: styled.div`
    padding: 16px 8px 16px 0;
    flex: 1;
    color: ${cloud.getShade(10)};
  `,
  CloseContainer: styled.div`
    flex: 0 0 ${CLOSE_SIZE}px;
  `,
};

const CloseButton = createThemedComponent(Button, {
  Button_minHeight_medium: CLOSE_SIZE,
});

const ToastContent: FunctionComponent<ToastProps> = ({
  type,
  onClose,
  children,
}) => {
  const intl = useIntl();

  return (
    <Styled.OuterContainer role="alert" type={type}>
      <Styled.IconContainer>{iconMap[type]}</Styled.IconContainer>
      <Styled.TextContainer>{children}</Styled.TextContainer>
      <Styled.CloseContainer>
        <CloseButton
          color="neutral"
          naked
          iconStart={<XSmall />}
          label={intl.formatMessage({ id: "general.close" })}
          fullWidth
          onClick={onClose}
        />
      </Styled.CloseContainer>
    </Styled.OuterContainer>
  );
};

export default ToastContent;
